import { useRef, useState, useEffect, useMemo } from "react"
import { useContentfulContext } from "../../contexts/ContentfulContext"
import { PrimaryButton, ErrorButton, SecondaryButton, DropdownButton, AgTableCard, Input } from "../UI";
import ManageRecommendationAddEntity from "./Modal/ManageRecommendationAddEntity"
import { useUserContext } from "../../contexts/UserContext"
import { deleteRecommendationByIds, getAllActivityData, getManageRecommendations } from "../../utils/APIs"
import Dropdown from "../UI/Dropdown/Dropdown"
import { ArrowLeftCircle, ArrowRightCircle, Search } from "lucide-react";
import EditManageRecommendation from "./Modal/EditManageRecommendation"
import ConfirmModal from "../UI/Modal/confirmModal"
import { toast } from "react-toastify"
import Loader from "../UI/Loader/Loader"
import BulkDeleteRecommendationModal from "./Modal/BulkDeleteRecommendationModal"

const ManageRecommendation = () => {
    const { content } = useContentfulContext()
    const { userData } = useUserContext();

    const [openAddEntityModal, setOpenAddEntityModal] = useState(false)
    const [environments, setEnvironments] = useState([])
    const [selectedEnvironment, setSelectedEnvironment] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(content?.MANAGE_RECOMMENDATION?.errors?.environment_required_for_entity)
    const [data, setData] = useState([])
    const [pagination, setPagination] = useState({
        start: 0,
        limit: 500,
        page: 0
    })
    const [gridApi, setGridApi] = useState(null);
    const [gridColumnApi, setGridColumnApi] = useState(null);
    const [selectedItems, setSelectedItems] = useState({});
    const [selectedRowData, setSelectedRowData] = useState(null);
    const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
    const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
    const [deleteFromAllEnvironments, setDeleteFromAllEnvironments] = useState(false);
    const [pageCache, setPageCache] = useState({});
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        getEnvironmentData()
    }, [userData?.defaultAdobeIO])

    const getEnvironmentData = async () => {
        const tenant_name = userData?.defaultAdobeIO?.tenant_name
        try {
            const result = await getAllActivityData(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                tenant_name,
                "environments",
            )
            if (result.status) {
                setEnvironments(result.data?.environments)
            }
        } catch (error) {
            console.log("error getAllIntegrationData: ", error)
            setError(error?.message || content?.COMMON_MESSAGE.something_wrong_msg)
        }
    }
    const filteredEnvironments = environments?.map((env) => ({
        id: env.id,
        name: env.name,
    }))

    const handleChooseEnvironment = (val) => {
        setSelectedEnvironment(val)
        setError("")
    }

    useEffect(() => {
        if (selectedEnvironment?.id && userData?.defaultAdobeIO) {
            setError("")
            getManageRecommendationsData(true)
        }
    }, [selectedEnvironment?.id])

    useEffect(() => {
        setData([])
        setPagination({ start: 0, limit: 500, page: 0 });
        setPageCache({});
        setError(content?.MANAGE_RECOMMENDATION?.errors?.environment_required_for_entity);
        setSelectedEnvironment(null);
        setEnvironments([]);
    }, [userData?.defaultAdobeIO])

    const getManageRecommendationsData = async (fetchFirstPage = false, refetchData = false, page = pagination.page) => {
        if (!refetchData && !fetchFirstPage && pageCache[page]) {
            return;
        }
        if (fetchFirstPage) page = 0;
        try {
            setLoading(true);
            const result = await getManageRecommendations({
                token: userData?.token,
                organizationId: userData?.selectedOrganization?.org_token,
                tenant: userData?.defaultAdobeIO?.tenant_name,
                environmentId: selectedEnvironment?.id,
                start: fetchFirstPage ? 0 : page * pagination.limit,
                limit: pagination.limit,
                search: searchQuery.trim() ? searchQuery.split(",").map(id => id.trim()).filter(id => id).join(",") : undefined,
            });
            setError("")
            if (result.data.hits?.length > 0) {
                if (fetchFirstPage) {
                    setPagination(prev => ({ ...prev, start: 0, page: 0 }));
                    setPageCache({ [page]: result.data.hits || [] });
                } else {
                    setPageCache(prev => ({
                        ...prev,
                        [page]: result.data.hits || [],
                    }));
                }
                setData(prev => [...prev, ...(result.data.hits || [])]);
            } else {
                toast.error("No more entities to load.");
                // setError("No entities found.");
                setPagination((prev) => ({ ...prev, start: prev.start - prev.limit, page: prev.page - 1 }))
            }
        } catch (error) {
            setError(error?.message || content?.COMMON_MESSAGE.something_wrong_msg)
        } finally {
            setLoading(false);
        }
    }

    const columns = [
        { headerName: "", field: "", checkboxSelection: true, headerCheckboxSelection: true, width: 40 },
        {
            headerName: content?.MANAGE_RECOMMENDATION?.columns_names?.thumbnail || "Thumbnail",
            field: "thumbnailUrl",
            resizable: true,
            minWidth: 100,
            cellRenderer: params => {
                return params.value ? <img src={params.value} alt="Thumbnail" style={{ width: '50px', height: '50px', objectFit: 'cover' }} /> : null;
            }
        },
        {
            headerName: content?.MANAGE_RECOMMENDATION?.columns_names?.id || "ID",
            field: "id",
            sortable: true,
            filter: true,
            resizable: true,
            minWidth: 160,
            cellRenderer: params => {
                return <span className="text-primary hover:underline cursor-pointer" onClick={() => setSelectedRowData(params.data)}>{params.value}</span>;
            }
        },
        {
            headerName: content?.MANAGE_RECOMMENDATION?.columns_names?.name || "Name",
            field: "name",
            sortable: true,
            filter: true,
            resizable: true,
            minWidth: 190,
        },
        {
            headerName: content?.MANAGE_RECOMMENDATION?.columns_names?.category || "Category",
            field: "category",
            sortable: true,
            filter: true,
            resizable: true,
            cellRenderer: params => params.data.category?.length ? params.data.category.join(", ") : null,
        },
        {
            headerName: content?.MANAGE_RECOMMENDATION?.columns_names?.pageUrl || "Page URL",
            field: "pageUrl",
            sortable: true,
            filter: true,
            resizable: true,
            cellRenderer: params => {
                return params.value ? <a className="text-primary hover:underline" href={params.value} target="_blank" rel="noopener noreferrer">{params.value}</a> : '';
            }
        },
        {
            headerName: content?.MANAGE_RECOMMENDATION?.columns_names?.value || "Value",
            field: "value",
            sortable: true,
            filter: true,
            resizable: true,
        },
        {
            headerName: content?.MANAGE_RECOMMENDATION?.columns_names?.brand || "Brand",
            field: "brand",
            sortable: true,
            filter: true,
            resizable: true,
        },
        {
            headerName: content?.MANAGE_RECOMMENDATION?.columns_names?.inventory || "Inventory",
            field: "inventory",
            sortable: true,
            filter: true,
            resizable: true,
        },
        {
            headerName: content?.MANAGE_RECOMMENDATION?.columns_names?.lastUpdatedAt || "Last Updated At",
            field: "lastUpdatedAt",
            sortable: true,
            filter: true,
            resizable: true,
            valueFormatter: params => new Date(params.value).toLocaleString(),
        },
        {
            headerName: content?.MANAGE_RECOMMENDATION?.columns_names?.createdAt || "Created At",
            field: "createdAt",
            sortable: true,
            filter: true,
            resizable: true,
            valueFormatter: params => new Date(params.value).toLocaleString(),
        },
    ];

    const gridApiRef = useRef(null);
    useEffect(() => {
        if (!gridApiRef.current) return;

        gridApiRef.current.forEachNode((node) => {
            const isSelected = selectedItems[pagination.page]?.find(item => item.id === node.data.id);
            node.setSelected(!!isSelected);
        });
    }, [pagination.page]);

    const onGridReady = (params) => {
        gridApiRef.current = params.api;
        setGridApi(params.api);
        setGridColumnApi(params.columnApi);
    };

    const autoSizeStrategy = useMemo(() => {
        return {
            type: window.innerWidth < 1024 ? "fitCellContents" : "fitGridWidth",
            defaultMinWidth: 150,
            columnLimits: columns.map((col) => ({
                colId: col.field,
                minWidth: col.minWidth || 100,
            })),
        };
    }, []);

    const handleRowSelected = (event) => {
        const selectedRows = event.api.getSelectedRows();
        setSelectedItems(prev => ({
            ...prev,
            [pagination.page]: selectedRows
        }));
    };

    const handleDeleteSelected = async () => {
        const allSelectedRows = Object.values(selectedItems).flat();
        if (allSelectedRows.length === 0) {
            toast.error(content?.MANAGE_RECOMMENDATION?.errors?.entity_required_for_deletion);
            return;
        }
        setLoading(true);
        try {
            const ids = allSelectedRows.map(item => item.id);
            const payloadData = {
                token: userData?.token,
                organizationId: userData?.selectedOrganization?.org_token,
                tenant: userData?.defaultAdobeIO?.tenant_name,
                ids: ids?.join(","),
            }
            if (!deleteFromAllEnvironments) {
                payloadData.environmentId = selectedEnvironment?.id;
            }
            const result = await deleteRecommendationByIds(payloadData);
            if (result.status) {
                toast.success(content?.MANAGE_RECOMMENDATION?.messages?.delete_success || "Entities deleted successfully");
                setSelectedItems({});
                setConfirmDeleteModalOpen(false);
                getManageRecommendationsData(true);
            } else {
                toast.error(result.message || content?.MANAGE_RECOMMENDATION?.messages?.delete_failed || "Failed to delete entities");
            }
        } catch (error) {
            console.error("Error deleting entities:", error);
            toast.error(content?.MANAGE_RECOMMENDATION?.messages?.delete_failed || "Failed to delete entities");
        } finally {
            setLoading(false);
        }
    }

    const onSearchByIds = () => {
        const ids = searchQuery.split(",").map(id => id.trim()).filter(id => id);
        if (searchQuery && ids.length === 0) {
            toast.error(content?.MANAGE_RECOMMENDATION?.errors?.entity_required_for_search || "Please enter valid entity IDs to search.");
            return;
        }
        setPagination({ start: 0, limit: pagination.limit, page: 0 });
        setPageCache({});
        getManageRecommendationsData(true, false, 0);
    }

    return (
        <>
            <h1 className="text-xl font-medium leading-tight tracking-tight text-gray-900 md:text-2xl">
                {content?.MANAGE_RECOMMENDATION?.title || "Manage Entities"}
            </h1>
            <p className="!m-0">{"Select an environment from the dropdown to list entities"}</p>
            <p className="!m-0">{"Note: Use a  comma-separated entity IDs for searching multiple entities"}</p>

            <AgTableCard
                header={
                    <div className="w-full flex items-start lg:items-center justify-between flex-col lg:flex-row gap-4">
                        <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
                            <div className="w-full sm:w-80">
                                <Input
                                    placeholder={content?.MANAGE_RECOMMENDATION?.labels?.search_placeholder}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                                    clearable
                                    onClear={() => setSearchQuery('')}
                                />
                            </div>
                            <SecondaryButton 
                                size={"sm"} 
                                handleClick={onSearchByIds}
                                className="w-full sm:w-auto"
                            >
                                {content?.MANAGE_RECOMMENDATION?.actions?.search || "Search"}
                            </SecondaryButton>
                        </div>

                        <div className="flex flex-col sm:flex-row justify-end gap-4 w-full lg:w-auto">
                            <Dropdown
                                width="190"
                                isWidthInPercentage={false}
                                items={filteredEnvironments}
                                callback={handleChooseEnvironment}
                                noItemSelectedTitle={content?.MANAGE_RECOMMENDATION?.labels?.select_environment || "Select Environment"}
                            />
                            <DropdownButton
                                title={content?.MANAGE_RECOMMENDATION?.labels?.actions || "Actions"}
                                buttons={[
                                    { title: content?.MANAGE_RECOMMENDATION?.cta?.add_entity, onClick: () => setOpenAddEntityModal(true) },
                                    pageCache[pagination.page]?.length > 0 && { title: content?.MANAGE_RECOMMENDATION?.actions?.bulk_delete, onClick: () => setBulkDeleteModalOpen(true) },
                                    Object.values(selectedItems).flat().length > 0 && { title: content?.MANAGE_RECOMMENDATION?.actions?.delete, onClick: () => setConfirmDeleteModalOpen(true) },
                                ].filter(Boolean)}
                            />
                        </div>
                    </div>
                }
                tableId="manage-recommendation-table"
                loading={loading}
                rowData={pageCache[pagination.page] || []}
                columnDefs={columns}
                defaultColDef={{
                    sortable: true,
                    filter: true,
                    resizable: true,
                }}
                pagination={false}
                autoSizeStrategy={autoSizeStrategy}
                onGridReady={onGridReady}
                rowSelection="multiple"
                suppressRowClickSelection
                onRowSelected={handleRowSelected}
                getRowNodeId={row => row.id}
                footer={
                    !loading && !error && pageCache[pagination.page]?.length > 0 ? (
                        <div className="ag-theme-quartz flex justify-center items-center py-4 gap-2 border-t border-border-table">
                            <ArrowLeftCircle
                                height={35}
                                width={35}
                                onClick={() => {
                                    if (pagination.page > 0) {
                                        getManageRecommendationsData(false, false, pagination.page - 1);
                                        setPagination((prev) => ({ ...prev, start: prev.start - prev.limit, page: prev.page - 1 }));
                                    }
                                }}
                                className={`cursor-pointer ${pagination.page === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                            />
                            <span className="ag-paging-page-summary">
                                Page {pagination.page + 1}
                            </span>
                            <ArrowRightCircle
                                height={35}
                                width={35}
                                onClick={() => {
                                    if ((pagination.page == 0) || (pageCache[pagination.page].length >= pagination.limit)) {
                                        getManageRecommendationsData(false, false, pagination.page + 1);
                                        setPagination((prev) => ({ ...prev, start: prev.start + prev.limit, page: prev.page + 1 }));
                                    }
                                }}
                                className={`cursor-pointer ${((pagination.page != 0) && (pageCache[pagination.page].length < pagination.limit)) ? 'opacity-50 cursor-not-allowed' : ''}`}
                            />
                        </div>
                    ) : null
                }
            >
                {error && !loading ? (
                    <h3 className="text-center py-12 text-stride-muted font-medium">{error}</h3>
                ) : null}
            </AgTableCard>

            {openAddEntityModal && (
                <ManageRecommendationAddEntity environments={filteredEnvironments} handleCloseAddEntity={() => setOpenAddEntityModal(false)} />
            )}

            {selectedRowData && (
                <EditManageRecommendation id={selectedRowData?.id} handleClose={() => setSelectedRowData(null)} refetchData={getManageRecommendationsData} />
            )}

            {bulkDeleteModalOpen && (
                <BulkDeleteRecommendationModal
                    selectedEnvironment={selectedEnvironment}
                    refetchData={getManageRecommendationsData}
                    handleClose={() => setBulkDeleteModalOpen(false)}
                />
            )}

            {confirmDeleteModalOpen && (
                <ConfirmModal
                    title={content?.MANAGE_RECOMMENDATION?.confirm_delete_title || "Confirm Deletion"}
                    ConfirmText={"Delete"}
                    description={<>
                        {content?.MANAGE_RECOMMENDATION?.confirm_delete || "Are you sure you want to delete the selected entities? This action cannot be undone."}
                        <div className="flex items-center gap-1 mt-2">
                            <input
                                type="checkbox"
                                checked={deleteFromAllEnvironments}
                                onChange={(e) => setDeleteFromAllEnvironments(e.target.checked)}
                                className="mr-2"
                            />
                            <p className="text-sm font-bold">
                                {content?.MANAGE_RECOMMENDATION?.delete_from_all_environments || "Delete From All Environments"}
                            </p>
                        </div>
                    </>}
                    error={error}
                    loading={loading}
                    hideModal={() => setConfirmDeleteModalOpen(false)}
                    handleConfirmation={handleDeleteSelected}
                />
            )}
        </>
    )
}

export default ManageRecommendation
